import { Component, Input, OnInit, inject } from '@angular/core';
import { IActivo, IRecorridoDinamico } from 'modelos/src';
import * as turf from '@turf/turf';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { IColumnas } from '../../../../../auxiliares/tabla/tabla.component';
import { fechaCelda } from '../../../../../auxiliares/tabla/celdas';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { RecorridosDinamicosService } from '../../../../../auxiliares/servicios/http/recorridos-dinamicos.service';
import {
  LineStringMapa,
  MapaFacilData,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { MapaFacilDialogComponent } from '../../../../../auxiliares/mapa-facil-dialog/mapa-facil-dialog.component';
import {
  DetalleCumplimientoPuntosComponent,
  DetalleCumplimientoPuntosData,
} from '../../recorridos/detalle-cumplimiento-puntos/detalle-cumplimiento-puntos.component';

/**
 * Historial de recorridos dinámicos (por puntos) que tuvo asignados este
 * vehículo, vigentes o ya finalizados. A diferencia del listado global de
 * recorridos dinámicos, acá siempre viene filtrado por `idActivo`.
 */
@Component({
  selector: 'app-historial-recorridos-dinamicos',
  templateUrl: './historial-recorridos-dinamicos.component.html',
  styleUrls: ['./historial-recorridos-dinamicos.component.scss'],
  standalone: false,
})
export class HistorialRecorridosDinamicosComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(RecorridosDinamicosService);
  private formularios = inject(FormularioDialogService);

  @Input() vehiculo?: IActivo;

  public loading = false;
  public recorridos?: IRecorridoDinamico[];
  public columnas: IColumnas<IRecorridoDinamico>[] = [];

  public async listar(): Promise<void> {
    if (!this.vehiculo?._id) return;

    this.loading = true;
    this.recorridos = undefined; // app-tabla muestra su loading sin datos
    try {
      const data = await this.service.getFiltered({
        filter: JSON.stringify({ idActivo: this.vehiculo._id }),
        sort: '-_id',
        limit: 0,
      });
      this.recorridos = data.datos;
    } catch (error) {
      console.error('Error al obtener el historial de recorridos:', error);
      this.helper.notifError('Error al obtener el historial de recorridos');
    }
    this.loading = false;
  }

  /** La ventana ya terminó — el recorrido quedó "Finalizado". */
  public estaFinalizado(recorrido: IRecorridoDinamico): boolean {
    const fechaHasta = recorrido.ventana?.fechaHasta;
    return !!fechaHasta && fechaHasta <= new Date().toISOString();
  }

  public async ubicar(r: IRecorridoDinamico): Promise<void> {
    const coords = r.geojson?.coordinates;
    if (!coords || coords.length === 0) {
      this.helper.notifError('El recorrido no tiene coordenadas');
      return;
    }

    const polyline: LineStringMapa = {
      color: r.color,
      points: coords,
      width: 6,
      centrar: true,
    };
    // Círculo con el radio real de tolerancia por parada, mismo criterio
    // que el mapa embebido de detalle de vehículo.
    const polygons =
      r.paradas
        ?.filter((p) => p.geojson?.coordinates)
        .map((parada, i) => {
          const circulo = turf.circle(
            parada.geojson!.coordinates as [number, number],
            parada.radioMetros ?? 60,
            { steps: 64, units: 'meters' },
          );
          return {
            points: circulo.geometry.coordinates as [number, number][][],
            color: r.color || '#1976d2',
            fillColor: r.color || '#1976d2',
            width: 2,
            label: `${i + 1}`,
          };
        }) ?? [];
    const data: MapaFacilData = {
      polylines: [polyline],
      polygons,
      vista: 'mapa',
      zoom: 14,
    };
    this.formularios.abrir(MapaFacilDialogComponent, {
      tamano: 'grande',
      data,
      cerrarAfuera: true,
    });
  }

  public verCumplimiento(recorrido: IRecorridoDinamico): void {
    this.formularios.abrir<
      DetalleCumplimientoPuntosComponent,
      DetalleCumplimientoPuntosData
    >(DetalleCumplimientoPuntosComponent, {
      tamano: 'mediano',
      data: { recorrido },
      cerrarAfuera: true,
    });
  }

  private setColumnas(): void {
    this.columnas = [
      {
        header: { label: 'Nombre' },
        row: { field: 'nombre', noData: 'Sin nombre' },
      },
      {
        header: { label: 'Ventana' },
        row: {
          parse(dato) {
            if (!dato.ventana?.fechaDesde) return undefined;
            const desde = fechaCelda(dato.ventana.fechaDesde);
            const hasta = dato.ventana.fechaHasta
              ? fechaCelda(dato.ventana.fechaHasta)
              : '';
            return `${desde} → ${hasta}`;
          },
          noData: 'Sin ventana',
        },
      },
      {
        header: { label: 'Estado' },
        row: {
          estado: (dato) =>
            this.estaFinalizado(dato)
              ? { texto: 'Finalizado', tono: 'neutro', icono: 'task_alt' }
              : { texto: 'Vigente', tono: 'ok', icono: 'toggle_on' },
        },
      },
      {
        header: { label: 'Cumplió' },
        row: {
          // Puntos visitados sobre el total, no solo sí/no: con 15 puntos y
          // freno en el 13, "13/15" dice mucho más que un "No" pelado.
          parse(dato) {
            const puntos = dato.detalleCumplimiento?.puntos;
            if (!puntos?.length) return undefined;
            const visitados = puntos.filter((p) => p.visitado).length;
            return `${visitados}/${puntos.length}`;
          },
          noData: 'Sin puntos',
        },
      },
      {
        header: { label: 'Acciones' },
        row: {
          acciones: [
            {
              tipo: 'material',
              color: 'primary',
              icon: 'map',
              tooltip: 'Ver en el mapa',
              click: (dato) => this.ubicar(dato),
            },
            {
              tipo: 'material',
              color: 'primary',
              icon: 'fact_check',
              tooltip: 'Ver cumplimiento',
              click: (dato) => this.verCumplimiento(dato),
            },
          ],
        },
      },
    ];
  }

  async ngOnInit(): Promise<void> {
    this.setColumnas();
    await this.listar();
  }
}
